import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stack, Box, Typography, Chip} from 'views/components';
import {Place, CalendarToday, Timer, Schedule, PersonOutline, TrendingUpOutlined, PhoneOutlined, PersonAddAlt1Outlined, EmailOutlined} from 'views/icons';
import {translate, Translate, AdminAutocomplete, LongStandardDateText} from 'views/module';

import {StageDropdown, CallStatusDropdown} from './dropdowns';

const ClientStatus = (props) => {
  const clientBackend = props.clientBackend;
  const clientData = props.clientData;
  const clientOwnerData = props.clientOwnerData?.data;
  const clientCallStatus = props.clientCallStatus;
  const clientForms = props.clientForms;
  const clientLogs = props.clientLogs;
  const clientCareplan = props.clientCareplan;
  const user = props.user;
  const [owner, setOwner] = React.useState(clientOwnerData);
  const [stage, setStage] = React.useState(clientData.status);
  const [callStatus, setCallStatus] = React.useState(clientCallStatus ?? '');

  const nullChar = '-';

  // Fetch forms (Recipient info, Schedule, Recipient)
  const clientRecipientInformationForm = clientForms.find((obj) => obj.formKey === 'CLIENT_RECIPIENT_INFORMATION_FORM');
  const clientScheduleIntakeForm = clientForms.find((obj) => obj.formKey === 'CLIENT_SCHEDULE_INTAKE_FORM');
  const clientRecipientForm = clientForms.find((obj) => obj.formKey === 'CLIENT_CARE_RECIPIENT');

  // Fetch client data
  const calendlyUserSlug = React.useMemo(() => {
    const userFirstName = user?.firstName?.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z0-9-]/g, '');
    return userFirstName ? `${userFirstName}-carequilt` : null;
  }, [user?.firstName]);

  const clientRelationships = React.useMemo(() => {
    return Object.values(clientData.relationships ?? {}).map((relationship) => {
      const relationshipName = (relationship.name ?? '').trim();
      const email = relationship.contacts?.find((x) => x?.type?.toLowerCase().includes('mail'))?.detail ?? '';
      const phoneNumber = relationship.contacts?.find((x) =>
        x?.type?.toLowerCase().includes('phone') ||
        x?.type?.toLowerCase().includes('number'),
      )?.detail ?? '';
      const [firstName = '', ...lastNameParts] = relationshipName.split(/\s+/).filter(Boolean);
      const params = new URLSearchParams({
        utm_term: relationship.relationshipRn ?? '',
        location: phoneNumber,
        name: relationshipName,
        email,
        utm_source: email,
        first_name: firstName,
        last_name: lastNameParts.join(' '),
      });

      return {
        ...relationship,
        calendlyLink: calendlyUserSlug ?
          `https://calendly.com/${calendlyUserSlug}?${params.toString()}` :
          null,
        email,
        phoneNumber,
      };
    });
  }, [clientData.relationships, calendlyUserSlug]);
  const clientCreatedAt = clientData.createdAt ?? null;
  const clientLocation = clientCareplan?.generalInformation?.address ? clientCareplan?.generalInformation?.address : nullChar;
  const clientDaysInStage = React.useMemo(() => {
    const log = Object.values(clientLogs).filter((log) => log.updateType === 'CLIENT_EDIT_STATUS');

    if (!log.length) {
      if (!clientCreatedAt) return nullChar;
      return Math.floor((Date.now() - new Date(clientCreatedAt))/86400000);
    }

    const obj = log.reduce((a, b) => {
      return new Date(a.createdAt) > new Date(b.createdAt) ? a : b;
    });
    const timeElapsed = (Date.now() - new Date(obj.createdAt).getTime());
    return Math.floor((timeElapsed)/86400000);
  }, [clientLogs, clientCreatedAt]);

  const clientStartTimeline = React.useMemo(() => {
    const startOptions = {
      'two-weeks': translate('client.ClientDetail.Status.SubtitleValue.TwoWeeks'),
      'four-weeks': translate('client.ClientDetail.Status.SubtitleValue.FourWeeks'),
      'unsure': nullChar,
    };
    return startOptions[clientScheduleIntakeForm?.answers['Schedule-start-time']?.value?.selectedOptionValue] ?? nullChar;
  }, [clientScheduleIntakeForm]);

  const clientHrsPerWeek = React.useMemo(() => {
    const hrsOptions = {
      '1-hour': 1,
      '2-3-hours': [2, 3],
      '4-6-hours': [4, 6],
      '8-12-hours': [8, 12],
      'other': translate('client.ClientDetail.Status.SubtitleValue.Other'),
    };
    const daysPerWeek = clientScheduleIntakeForm?.answers['Schedule-days']?.value?.selectedOptionValues?.length;
    const selectedHrsPerDay = clientScheduleIntakeForm?.answers['Schedule-visit-duration']?.value?.selectedOptionValue;
    const hrsPerDay = hrsOptions[selectedHrsPerDay] ?? nullChar;
    if (typeof(hrsPerDay) !== 'string') {
      if (hrsPerDay.length > 1) {
        return hrsPerDay && daysPerWeek ? `${hrsPerDay[0] * daysPerWeek}-${hrsPerDay[1] * daysPerWeek}` : nullChar;
      } else {
        return hrsPerDay && daysPerWeek ? hrsPerDay * daysPerWeek : nullChar;
      }
    } else {
      return hrsPerDay;
    }
  }, [clientScheduleIntakeForm]);

  const clientRecipientRelationship = React.useMemo(() => {
    const recipientOptions = {
      'parents': translate('client.ClientDetail.Status.SubtitleValue.Parent'),
      'spouse': translate('client.ClientDetail.Status.SubtitleValue.Spouse'),
      'self': translate('client.ClientDetail.Status.SubtitleValue.Self'),
    };

    // Only fetching from forms
    return recipientOptions[clientRecipientForm?.answers['Care_Recipient']?.value?.selectedOptionValue] ?? null;
  }, [clientRecipientForm]);

  const clientRecipientName = React.useMemo(() => {
    // Fetching client first from careplan
    if (clientCareplan?.generalInformation?.clientName) {
      return clientCareplan.generalInformation.clientName;
    }

    // Secondary fetching from forms
    return clientRecipientForm?.answers['Care_Recipient']?.value?.selectedOptionValue !== 'self' &&
    (clientRecipientInformationForm?.answers?.Name?.value?.text || nullChar);
  }, [clientRecipientInformationForm, clientRecipientForm]);

  const clientMatch = React.useMemo(() => {
    return Object.keys(translate('client.MatchDifficulty')).includes(clientCareplan?.caregiverRequirements?.matchDifficulty) ?
    clientCareplan?.caregiverRequirements?.matchDifficulty : nullChar;
  }, [clientCareplan]);

  // Handlers

  const handleSetOwner = (selectedAdmin) => {
    if (!selectedAdmin) return;
    const selectedAdminRn = selectedAdmin.userRn;
    setOwner(selectedAdmin);

    clientBackend.detail.editClientOwner(selectedAdminRn, () => {});
  };

  const handleSetStage = (selectedStage) => {
    if (!selectedStage) return;
    setStage(selectedStage);

    clientBackend.detail.editStatus(selectedStage, () => {});
  };

  const handleSetCallStatus = (selectedCallStatus) => {
    if (!selectedCallStatus) return;
    setCallStatus(selectedCallStatus);

    clientBackend.detail.editCallStatus(selectedCallStatus, () => {});
  };


  return (
    <Box sx={{width: '100%', m: 2}}>
      <Stack direction="row">
        {/* Left side */}
        <Stack direction="column" sx={{flex: 1}}>
          {/* Location Header */}
          <Box sx={{mb: 2}}>
            {/* Location */}
            <Typography variant="body2" color="textSecondary" component="div">
              <Place fontSize='1rem' sx={{mr: 1}} />
              {clientLocation}
            </Typography>
          </Box>
          {/* Status displays */}
          <Box
            display="grid"
            sx={{
              'flex': 1,
              'gap': 1,
              'gridTemplateColumns': {
                xs: 'repeat(1, minmax(8rem, 1fr))',
                sm: 'repeat(2, minmax(8rem, 1fr))',
                md: 'repeat(3, minmax(8rem, 1fr))',
              },
              '& > :last-child': {
                gridColumn: '1 / -1',
              },
            }}
          >

            {/* Created at */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <CalendarToday fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.Created" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {
                  clientCreatedAt ?
                  <LongStandardDateText date={clientCreatedAt} /> : nullChar
                }
              </Typography>
            </Box>
            {/* Days in stage */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Timer fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.DaysInStage" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {
                  typeof(clientDaysInStage) === 'number' &&
                  <Translate
                    expression={`client.ClientDetail.Status.SubtitleValue.${clientDaysInStage === 1 ? 'Day' : 'Days'}`}
                    substitute={{number: clientDaysInStage}}
                  />
                }
                {
                  (typeof(clientDaysInStage) === 'string') &&
                  <>{clientDaysInStage}</>
                }
              </Typography>
            </Box>
            {/* Start Timeline */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Schedule fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.StartTimeline" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {clientStartTimeline}
              </Typography>
            </Box>
            {/* Hrs/Week */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <Schedule fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.HrsPerWeek" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {clientHrsPerWeek}
              </Typography>
            </Box>
            {/* Care Recipient */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <PersonOutline fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.CareRecipient" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                <>
                  {clientRecipientName}
                  {
                    (clientRecipientRelationship) && (
                      <Typography variant="body2" color="textSecondary" component="span" sx={{ml: .5, wordBreak: 'keep-all'}}>
                        {clientRecipientRelationship}
                      </Typography>
                    )
                  }
                </>
              </Typography>
            </Box>
            {/* Match Difficulty */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <TrendingUpOutlined fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.MatchDifficulty" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                {(() => {
                  if (clientMatch != nullChar) {
                    return (
                      <Chip
                        label={<Translate expression={`client.MatchDifficulty.${clientMatch}.Title`} />}
                        color={translate(`client.MatchDifficulty.${clientMatch}.Color`)}
                        variant="outlined"
                      />
                    );
                  } else {
                    return clientMatch;
                  }
                })()}
              </Typography>
            </Box>
            {/* Relationships */}
            <Box component="span">
              <Stack direction="row" sx={{alignItems: 'center'}}>
                <PersonAddAlt1Outlined fontSize='1rem' sx={{mr: 1}} />
                <Typography variant="body2" color="textSecondary" component="div">
                  <Translate expression="client.ClientDetail.Status.Subtitles.Relationships" />
                </Typography>
              </Stack>
              <Typography variant="body1" color="textPrimary" sx={{ml: 3, wordBreak: 'break-word'}} component="div">
                <Stack direction="column" sx={{gap: 2}}>
                  {
                    clientRelationships.length !== 0 &&
                      clientRelationships.map((person, i) => (
                        <Box key={i} sx={{borderLeft: 1, borderColor: 'divider', pl: 1}}>
                          <Stack direction="row" sx={{alignItems: 'baseline'}}>
                            <Typography>{person.name}</Typography>
                            <Typography variant="body2" color="textSecondary" component="span" sx={{ml: 1, wordBreak: 'keep-all'}}>
                              {person.connectionType}
                            </Typography>
                          </Stack>
                          {
                            person.contacts &&
                            <Stack direction="row" sx={{alignItems: 'center'}}>
                              <EmailOutlined fontSize='1rem' sx={{mr: 1}}/>
                              <Typography>
                                {person.email || nullChar}
                              </Typography>
                            </Stack>
                          }
                          {
                            person.contacts &&
                            <Stack direction="row" sx={{alignItems: 'center'}}>
                              <PhoneOutlined fontSize='1rem' sx={{mr: 1}}/>
                              <Typography>
                                {person.phoneNumber || nullChar}
                              </Typography>
                            </Stack>
                          }
                          {
                            person.calendlyLink &&
                            <Stack direction="row" sx={{alignItems: 'center'}}>
                              <CalendarToday fontSize='1rem' sx={{mr: 1}}/>
                              <Typography
                                component="a"
                                href={person.calendlyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  'color': 'primary.main',
                                  'fontWeight': 500,
                                  'textDecoration': 'none',
                                  'transition': 'color 0.2s ease',
                                  '&:hover': {
                                    color: 'primary.dark',
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                <Translate expression="client.ClientDetail.Status.Subtitles.BookWithCalendly" />
                              </Typography>
                            </Stack>
                          }
                        </Box>
                      ))
                  }
                  {
                    !clientRelationships.length &&
                    <>{nullChar}</>
                  }
                </Stack>
              </Typography>
            </Box>
          </Box>
        </Stack>
        {/* Right side */}
        <Box sx={{ml: 'auto', width: '14rem', mb: 'auto', p: 2}}>
          <Stack direction="column" sx={{gap: 2, height: '100%', justifyContent: 'center'}} spacing={2}>
            {/* Autocomplete Panels */}
            <AdminAutocomplete
              label={<Translate expression="client.ClientDetail.Status.Menu.Subtitles.Owner" />}
              selectedAdmin={owner}
              setSelectedAdmin={handleSetOwner}
            />
            <StageDropdown
              selectedStage={stage}
              setSelectedStage={handleSetStage}
            />
            <CallStatusDropdown
              selectedStatus={callStatus}
              setSelectedStatus={handleSetCallStatus}
            />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
ClientStatus.propTypes = {
  clientRn: PropTypes.string.isRequired,
  clientData: PropTypes.object.isRequired,
  clientOwnerData: PropTypes.object,
  clientBackend: PropTypes.object.isRequired,
  clientForms: PropTypes.array,
  clientCareplan: PropTypes.object,
  clientLogs: PropTypes.object.isRequired,
  clientCallStatus: PropTypes.string,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientStatus));
